import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FiPlay, 
  FiRotateCw, 
  FiAlertTriangle, 
  FiCheckCircle, 
  FiXCircle,
  FiClock,
  FiMapPin
} from 'react-icons/fi';
import { GiCommercialAirplane } from 'react-icons/gi';
import { MdFlightTakeoff, MdFlightLand } from 'react-icons/md';
import Sidebar from '../../components/ui/Sidebar';
import FlightEditorModal from '../../components/FlightEditorModal';
import { useTheme } from '../../contexts/ThemeContext';
import './simulator.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const RerouteSimulator = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Rotation selection
  const [rotationNumber, setRotationNumber] = useState(searchParams.get('rotation') || '');
  const [rotations, setRotations] = useState([]);
  const [loadingRotations, setLoadingRotations] = useState(false);
  
  // Simulation state
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modifications, setModifications] = useState({
    scheduled_departure: '',
    scheduled_arrival: ''
  });
  
  // UI state
  const [impactVisible, setImpactVisible] = useState(true);

  // Breadcrumb data
  const breadcrumb = [
    { label: 'Reroute Lab', path: '/reroute-lab' },
    { label: 'Simulator', icon: 'GitBranch' }
  ];

  // Load available rotations on mount
  useEffect(() => {
    loadRotations();
  }, []);

  // Debug: Log session state changes
  useEffect(() => {
    console.log('🔄 [DEBUG] Session state changed:', session);
    if (session) {
      console.log('📌 Session ID:', session.session_id);
      console.log('📌 Has rotation_data?', !!session.rotation_data);
      if (session.rotation_data) {
        console.log('📌 Duty periods:', session.rotation_data.duty_periods?.length || 0);
        console.log('📌 Full rotation_data structure:', session.rotation_data);
      } else {
        console.warn('⚠️ rotation_data is MISSING!');
      }
    } else {
      console.log('⚠️ Session is NULL');
    }
  }, [session]);

  const loadRotations = async () => {
    setLoadingRotations(true);
    try {
      // Get employee_id from user settings (you may need to adjust this)
      const employeeId = '141986'; // TODO: Get from auth context
      const response = await fetch(`${API_BASE_URL}/api/v1/rotations?employee_id=${employeeId}&limit=50`);
      if (!response.ok) throw new Error('Failed to load rotations');
      const data = await response.json();
      setRotations(data.rotations || []);
    } catch (err) {
      console.error('Error loading rotations:', err);
      setError('Failed to load rotations. Please try again.');
    } finally {
      setLoadingRotations(false);
    }
  };

  const createSession = async () => {
    if (!rotationNumber.trim()) {
      setError('Please enter a rotation number');
      return;
    }

    setLoading(true);
    setError(null);

    console.log('🚀 [DEBUG] Creating session for rotation:', rotationNumber);

    try {
      const requestBody = {
        rotation_number: rotationNumber,
        employee_id: '141986',
        employee_name: 'Test Pilot',
        user_id: 'test-user-id'
      };
      console.log('📤 [DEBUG] Request body:', requestBody);

      const response = await fetch(`${API_BASE_URL}/api/v1/simulator/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('📥 [DEBUG] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ [DEBUG] API Error:', errorData);
        throw new Error(errorData.detail || 'Failed to create session');
      }

      const data = await response.json();
      console.log('✅ [DEBUG] Session created successfully!');
      console.log('📊 [DEBUG] Full response data:', data);
      console.log('🔑 [DEBUG] Session ID:', data.session_id);
      console.log('📋 [DEBUG] Rotation data:', data.rotation_data);
      console.log('🎯 [DEBUG] Metadata:', data.metadata);
      
      // Check if rotation_data exists and has duty_periods
      if (data.rotation_data) {
        console.log('✓ rotation_data EXISTS');
        console.log('📍 Duty periods count:', data.rotation_data.duty_periods?.length || 0);
        if (data.rotation_data.duty_periods && data.rotation_data.duty_periods.length > 0) {
          console.log('✓ Found', data.rotation_data.duty_periods.length, 'duty periods');
          console.log('📍 First duty period:', data.rotation_data.duty_periods[0]);
        } else {
          console.warn('⚠️ No duty_periods in rotation_data!');
        }
      } else {
        console.error('❌ rotation_data is MISSING from response!');
      }
      
      setSession(data);
      setError(null);
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.message || 'Failed to create simulation session');
    } finally {
      setLoading(false);
    }
  };

  const executeModification = async () => {
    if (!session) return;
    if (!selectedFlight) {
      setError('Please select a flight to modify');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/simulator/sessions/${session.session_id}/execute`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'modify_flight',
            flight_id: selectedFlight.leg_sequence,
            modifications: {
              scheduled_departure: modifications.scheduled_departure || selectedFlight.sched_out?.local_wall,
              scheduled_arrival: modifications.scheduled_arrival || selectedFlight.sched_in?.local_wall
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to execute modification');
      }

      const data = await response.json();
      setSession(data); // Update session with new state
      
      // Clear modifications
      setModifications({
        scheduled_departure: '',
        scheduled_arrival: ''
      });
      setSelectedFlight(null);
    } catch (err) {
      console.error('Error executing modification:', err);
      setError(err.message || 'Failed to execute modification');
    } finally {
      setLoading(false);
    }
  };

  const handleFlightUpdate = async (modifiedFlight) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/simulator/sessions/${session.session_id}/execute`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'modify_flight',
            flight_id: modifiedFlight.leg_sequence,
            modifications: {
              scheduled_departure: modifiedFlight.scheduled_out_time,
              scheduled_arrival: modifiedFlight.scheduled_in_time,
              flight_number: modifiedFlight.flight_number,
              origin: modifiedFlight.origin,
              destination: modifiedFlight.destination,
              aircraft_model: modifiedFlight.aircraft_model,
              is_deadhead: modifiedFlight.is_deadhead
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update flight');
      }

      const data = await response.json();
      setSession(data);
      setIsModalOpen(false);
      setSelectedFlight(null);
    } catch (err) {
      console.error('Error updating flight:', err);
      setError(err.message || 'Failed to update flight');
    } finally {
      setLoading(false);
    }
  };

  const resetSession = () => {
    setSession(null);
    setSelectedFlight(null);
    setModifications({
      scheduled_departure: '',
      scheduled_arrival: ''
    });
    setError(null);
  };

  // Get compliance status indicator
  const getComplianceIndicator = () => {
    if (!session?.compliance_status) return null;

    const status = session.compliance_status;
    
    if (status === 'compliant') {
      return {
        color: 'green',
        icon: <FiCheckCircle />,
        label: 'COMPLIANT',
        message: 'All FAR 117 limits satisfied'
      };
    } else if (status === 'warning') {
      return {
        color: 'yellow',
        icon: <FiAlertTriangle />,
        label: 'WARNING',
        message: 'Approaching FAR 117 limits'
      };
    } else {
      return {
        color: 'red',
        icon: <FiXCircle />,
        label: 'VIOLATION',
        message: 'FAR 117 limits exceeded - DO NOT FLY'
      };
    }
  };

  const complianceIndicator = getComplianceIndicator();

  return (
    <div className={`simulator-container ${theme}`}>
      <Sidebar />
      
      <div className="simulator-content">
        {/* Header */}
        <div className="simulator-header">
          <div className="header-left">
            <GiCommercialAirplane className="header-icon" />
            <div>
              <h1>Reroute Simulator</h1>
              <p className="subtitle">Test schedule changes before accepting reroutes</p>
            </div>
          </div>
          <button 
            className="btn-back"
            onClick={() => navigate('/reroute-lab')}
          >
            Back to Lab
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <FiAlertTriangle />
            <span>{error}</span>
          </div>
        )}

        {/* Session Creation / Selection */}
        {!session && (
          <div className="card">
            <h2>Start Simulation</h2>
            <p className="card-description">
              Select a rotation to simulate schedule changes and see the impact on FAR 117 compliance and pay.
            </p>
            
            <div className="rotation-selector">
              <label>Rotation Number</label>
              <div className="input-group">
                <input
                  type="text"
                  value={rotationNumber}
                  onChange={(e) => setRotationNumber(e.target.value)}
                  placeholder="Enter rotation number (e.g., 4302)"
                  disabled={loading}
                />
                <button
                  className="btn-primary"
                  onClick={createSession}
                  disabled={loading || !rotationNumber.trim()}
                >
                  {loading ? 'Creating...' : 'Start Simulation'}
                </button>
              </div>
            </div>

            {loadingRotations && <p className="loading-text">Loading your rotations...</p>}
            
            {rotations.length > 0 && (
              <div className="recent-rotations">
                <h3>Recent Rotations</h3>
                <div className="rotation-grid">
                  {rotations.slice(0, 6).map((rot) => (
                    <button
                      key={rot.id}
                      className="rotation-card"
                      onClick={() => setRotationNumber(rot.rotation_number)}
                    >
                      <span className="rotation-number">{rot.rotation_number}</span>
                      <span className="rotation-dates">
                        {rot.rotation_start_date} → {rot.rotation_end_date}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active Simulation */}
        {session && (
          <>
            {/* Compliance Status Banner */}
            {complianceIndicator && (
              <div className={`compliance-banner ${complianceIndicator.color}`}>
                <div className="compliance-content">
                  {complianceIndicator.icon}
                  <div>
                    <span className="compliance-label">{complianceIndicator.label}</span>
                    <span className="compliance-message">{complianceIndicator.message}</span>
                  </div>
                </div>
                {session.warnings && session.warnings.length > 0 && (
                  <div className="warnings-list">
                    {session.warnings.map((warning, idx) => (
                      <div key={idx} className="warning-item">
                        <FiAlertTriangle />
                        <span>{warning}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="simulation-grid">
              {/* Flight Editor */}
              <div className="card flight-editor">
                <div className="card-header">
                  <h2>Flight Editor</h2>
                  <button 
                    className="btn-secondary btn-sm"
                    onClick={resetSession}
                  >
                    <FiRotateCw /> New Simulation
                  </button>
                </div>

                <div className="session-info">
                  <span className="info-label">Session ID:</span>
                  <span className="info-value">{session.session_id}</span>
                </div>

                <div className="space-y-6">
                  {session.rotation_data?.duty_periods?.map((dp, dpIdx) => {
                    // Helper functions (copied from FlightTimeline.jsx)
                    const formatTimeWithZone = (timeObj, showZone = 'local') => {
                      if (!timeObj) return 'N/A';
                      const utcString = typeof timeObj === 'string' ? timeObj : timeObj.utc;
                      const localString = timeObj.local_wall;
                      if (!utcString) return 'N/A';
                      
                      let timeStr = '';
                      let suffix = '';
                      
                      if (showZone === 'utc' || showZone === 'zulu') {
                        const date = new Date(utcString);
                        timeStr = date.toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: false,
                          timeZone: 'UTC'
                        });
                        suffix = 'Z';
                      } else if (showZone === 'local') {
                        const date = new Date(localString || utcString);
                        timeStr = date.toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: false 
                        });
                        suffix = 'L';
                      } else if (showZone === 'base') {
                        const date = new Date(localString || utcString);
                        timeStr = date.toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: false 
                        });
                        suffix = 'B';
                      }
                      
                      return `${timeStr}${suffix}`;
                    };

                    const formatDate = (timeObj) => {
                      if (!timeObj) return 'N/A';
                      const isoString = typeof timeObj === 'string' ? timeObj : timeObj.utc || timeObj.local_wall;
                      if (!isoString) return 'N/A';
                      const date = new Date(isoString);
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      });
                    };

                    const calculateDuration = (start, end) => {
                      if (!start || !end) return 'N/A';
                      const startStr = typeof start === 'string' ? start : start.utc || start.local_wall;
                      const endStr = typeof end === 'string' ? end : end.utc || end.local_wall;
                      if (!startStr || !endStr) return 'N/A';
                      const startTime = new Date(startStr);
                      const endTime = new Date(endStr);
                      const diffMinutes = Math.round((endTime - startTime) / 60000);
                      const hours = Math.floor(diffMinutes / 60);
                      const minutes = diffMinutes % 60;
                      return `${hours}:${minutes.toString().padStart(2, '0')}`;
                    };

                    const calculateConnectionMinutes = (arrivalTime, departureTime) => {
                      if (!arrivalTime || !departureTime) return null;
                      const arrStr = typeof arrivalTime === 'string' ? arrivalTime : arrivalTime.utc || arrivalTime.local_wall;
                      const depStr = typeof departureTime === 'string' ? departureTime : departureTime.utc || departureTime.local_wall;
                      if (!arrStr || !depStr) return null;
                      const arrival = new Date(arrStr);
                      const departure = new Date(depStr);
                      return Math.round((departure - arrival) / 60000);
                    };

                    return (
                      <div key={dpIdx} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                        {/* Duty Period Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-bold">
                                Day {dp.duty_sequence} - {formatDate(dp.report)}
                              </h3>
                              <div className="text-sm opacity-90 mt-1 font-mono">
                                <span className="mr-1">Report:</span>
                                <span className="mr-2">{formatTimeWithZone(dp.report, 'local')}</span>
                                <span className="mr-2">{formatTimeWithZone(dp.report, 'zulu')}</span>
                                <span className="mr-3">{formatTimeWithZone(dp.report, 'base')}</span>
                                <span className="mr-1">→ Release:</span>
                                <span className="mr-2">{formatTimeWithZone(dp.release, 'local')}</span>
                                <span className="mr-2">{formatTimeWithZone(dp.release, 'zulu')}</span>
                                <span>{formatTimeWithZone(dp.release, 'base')}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold">
                                {calculateDuration(dp.report, dp.release)}
                              </p>
                              <p className="text-xs opacity-90">Duty Time</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Equipment Badge */}
                        {session.rotation_data?.meta?.equipment && (
                          <div className="px-4 pt-3">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                              ✈️ Equipment: {session.rotation_data.meta.equipment}
                            </span>
                          </div>
                        )}
                        
                        {/* Flight Legs */}
                        <div className="p-4 space-y-3">
                          {dp.legs && dp.legs.map((leg, legIdx) => {
                            const isDeadhead = leg.is_deadhead || leg.flight_type === 'DEADHEAD';
                            const isSelected = selectedFlight?.leg_sequence === leg.leg_sequence;
                            
                            return (
                              <div 
                                key={legIdx}
                                className={`
                                  border-2 rounded-lg p-4 cursor-pointer transition-all
                                  ${isDeadhead 
                                    ? 'border-amber-300 bg-amber-50' 
                                    : isSelected
                                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                                  }
                                `}
                                onClick={() => {
                                  setSelectedFlight(leg);
                                  setIsModalOpen(true);
                                }}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {isDeadhead && (
                                      <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded">
                                        DH
                                      </span>
                                    )}
                                    {isSelected && (
                                      <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded">
                                        SELECTED
                                      </span>
                                    )}
                                    <span className="font-mono font-bold text-lg text-gray-900">
                                      {leg.flight_number || 'N/A'}
                                    </span>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-sm text-gray-600">
                                      Leg {legIdx + 1} of {dp.legs.length}
                                    </span>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-gray-900">
                                      {leg.sched_block?.minutes 
                                        ? `${Math.floor(leg.sched_block.minutes / 60)}:${(leg.sched_block.minutes % 60).toString().padStart(2, '0')}`
                                        : calculateDuration(leg.sched_out, leg.sched_in)
                                      }
                                    </p>
                                    <p className="text-xs text-gray-500">Block Time</p>
                                  </div>
                                </div>
                                
                                {/* Route */}
                                <div className="flex items-center gap-4 mb-3">
                                  <div className="flex-1">
                                    <p className="text-xs text-gray-500 uppercase">From</p>
                                    <p className="text-2xl font-bold text-gray-900">{leg.origin || leg.departure || 'N/A'}</p>
                                    <div className="text-sm text-gray-600 font-mono">
                                      <span className="mr-2">{formatTimeWithZone(leg.sched_out, 'local')}</span>
                                      <span className="mr-2">{formatTimeWithZone(leg.sched_out, 'zulu')}</span>
                                      <span>{formatTimeWithZone(leg.sched_out, 'base')}</span>
                                    </div>
                                  </div>
                                  
                                  <div className="text-2xl text-gray-300">→</div>
                                  
                                  <div className="flex-1 text-right">
                                    <p className="text-xs text-gray-500 uppercase">To</p>
                                    <p className="text-2xl font-bold text-gray-900">{leg.destination || leg.arrival || 'N/A'}</p>
                                    <div className="text-sm text-gray-600 font-mono">
                                      <span className="mr-2">{formatTimeWithZone(leg.sched_in, 'local')}</span>
                                      <span className="mr-2">{formatTimeWithZone(leg.sched_in, 'zulu')}</span>
                                      <span>{formatTimeWithZone(leg.sched_in, 'base')}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Aircraft Type */}
                                {leg.equipment && (
                                  <div className="text-xs text-gray-500">
                                    Aircraft: <span className="font-mono text-gray-700">{leg.equipment}</span>
                                  </div>
                                )}
                                
                                {/* Connection Time (if not last leg in duty period) */}
                                {legIdx < dp.legs.length - 1 && (() => {
                                  const nextLeg = dp.legs[legIdx + 1];
                                  const connectionMinutes = calculateConnectionMinutes(leg.sched_in, nextLeg.sched_out);
                                  if (connectionMinutes !== null) {
                                    const hours = Math.floor(connectionMinutes / 60);
                                    const minutes = connectionMinutes % 60;
                                    const isShortConnection = connectionMinutes < 60;
                                    
                                    return (
                                      <div className={`
                                        mt-3 pt-3 border-t border-dashed
                                        ${isShortConnection ? 'border-red-300' : 'border-gray-300'}
                                      `}>
                                        <div className="flex items-center justify-between text-sm">
                                          <span className="text-gray-600">
                                            ⏱️ Connection Time to Next Flight
                                          </span>
                                          <span className={`
                                            font-mono font-semibold
                                            ${isShortConnection ? 'text-red-600' : 'text-gray-700'}
                                          `}>
                                            {hours}:{minutes.toString().padStart(2, '0')}
                                            {isShortConnection && ' ⚠️'}
                                          </span>
                                        </div>
                                        {isShortConnection && (
                                          <p className="text-xs text-red-600 mt-1">
                                            Short connection - less than 1 hour
                                          </p>
                                        )}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Layover Card (if overnight) */}
                        {dp.layover_hotel_name && (
                          <div className="mx-4 mb-4 border-t-2 border-dashed border-gray-200 pt-4">
                            <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-300 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <span className="text-2xl flex-shrink-0 mt-1">🏨</span>
                                <div className="flex-1">
                                  <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                                    🏨 Layover Hotel
                                    {dp.layover_duration && (
                                      <span className="text-xs font-normal text-green-700 bg-green-200 px-2 py-1 rounded">
                                        {dp.layover_duration.minutes 
                                          ? `${Math.floor(dp.layover_duration.minutes / 60)}:${(dp.layover_duration.minutes % 60).toString().padStart(2, '0')}`
                                          : dp.layover_duration
                                        }
                                      </span>
                                    )}
                                  </h4>
                                  
                                  <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                      <span className="text-green-600">📍</span>
                                      <span className="font-semibold text-green-900">
                                        {dp.layover_hotel_name}
                                      </span>
                                      {dp.layover_location && (
                                        <span className="text-green-700">
                                          ({dp.layover_location})
                                        </span>
                                      )}
                                    </div>
                                    
                                    {dp.layover_phone && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-green-600">📞</span>
                                        <a 
                                          href={`tel:${dp.layover_phone}`}
                                          className="text-green-700 hover:text-green-900 hover:underline"
                                        >
                                          {dp.layover_phone}
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedFlight && (
                  <FlightEditorModal
                    isOpen={isModalOpen}
                    flight={selectedFlight}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleFlightUpdate}
                  />
                )}
              </div>

              {/* Impact Display */}
              {impactVisible && (
                <div className="card impact-display">
                  <div className="card-header">
                    <h2>Impact Analysis</h2>
                    <button 
                      className="btn-icon"
                      onClick={() => setImpactVisible(false)}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Pay Impact */}
                  <div className="impact-section">
                    <h3>Pay Impact</h3>
                    {session.pay_impact ? (
                      <div className="pay-grid">
                        <div className="pay-item">
                          <span className="pay-label">Credit Hours</span>
                          <span className="pay-value">
                            {session.pay_impact.credit_hours?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <div className="pay-item">
                          <span className="pay-label">Pay Hours</span>
                          <span className="pay-value">
                            {session.pay_impact.pay_hours?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        {session.pay_impact.delta && (
                          <div className={`pay-delta ${session.pay_impact.delta > 0 ? 'positive' : 'negative'}`}>
                            <span>Change:</span>
                            <span>
                              {session.pay_impact.delta > 0 ? '+' : ''}
                              {session.pay_impact.delta.toFixed(2)} hrs
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="no-data">No pay impact data available</p>
                    )}
                  </div>

                  {/* FAR 117 Compliance */}
                  <div className="impact-section">
                    <h3>FAR 117 Compliance</h3>
                    {session.compliance_status ? (
                      <div className="compliance-details">
                        <div className="compliance-row">
                          <span>Status:</span>
                          <span className={`status-badge ${session.compliance_status}`}>
                            {session.compliance_status?.toUpperCase()}
                          </span>
                        </div>
                        
                        {session.compliance_checks && (
                          <div className="checks-list">
                            {Object.entries(session.compliance_checks).map(([check, passed]) => (
                              <div key={check} className="check-item">
                                {passed ? <FiCheckCircle className="check-pass" /> : <FiXCircle className="check-fail" />}
                                <span>{check.replace(/_/g, ' ')}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="no-data">No compliance data available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RerouteSimulator;
